import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import { DomainsTable } from "./DomainsTable";
import { AddDomainForm } from "./AddDomainForm";
import type { Tenant } from "../types/AdminTypes";
import { useClerk } from '@clerk/clerk-react';
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { httpRequest } from '@/lib/http-client';

interface DomainsManagementProps {
  tenant: Tenant;
}

export function DomainsManagement({ tenant }: DomainsManagementProps) {
  const { session } = useClerk();
  const [newDomain, setNewDomain] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: domains, isLoading: isLoadingDomains, error: domainsError } = useQuery({
    queryKey: ['domains', tenant.id],
    queryFn: async () => {
      const token = await session?.getToken();
      const response = await httpRequest(`/api/admin/domains/${tenant.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch domains');
      }
      return response.json();
    },
  });

  const createDomain = useMutation({
    mutationFn: async (domain: string) => {
      const token = await session?.getToken();
      const response = await httpRequest('/api/admin/domains', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        data: { domain, tenantId: tenant.id }
      });
      if (!response.ok) {
        throw new Error('Failed to add domain');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains', tenant.id] });
      setNewDomain("");
      toast({
        title: "Success",
        description: "Domain added successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add domain",
        variant: "destructive",
      });
    },
  });

  const deleteDomain = useMutation({
    mutationFn: async (id: string) => {
      const token = await session?.getToken();
      const response = await httpRequest(`/api/admin/domains/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error('Failed to delete domain');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains', tenant.id] });
      toast({
        title: "Success",
        description: "Domain deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete domain",
        variant: "destructive",
      });
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Whitelisted Domains</h3>
        <Dialog>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Domain
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Whitelisted Domain</DialogTitle>
            </DialogHeader>
            <AddDomainForm
              newDomain={newDomain}
              onDomainChange={setNewDomain}
              onSubmit={() => createDomain.mutate(newDomain)}
              isSubmitting={createDomain.isPending}
            />
          </DialogContent>
        </Dialog>
      </div>

      <DomainsTable
        domains={domains}
        isLoading={isLoadingDomains}
        error={domainsError}
        onDelete={(id) => deleteDomain.mutate(id)}
      />
    </div>
  );
}